/**
 * Autonomy facade test-id registry (J6, Wave 3).
 *
 * Single source of truth for every `data-testid` this module renders.
 * JSX must import from here — no test-id literals in components
 * (shared-test-navigation rule). Names mirrored in the @test-kit
 * `AUTONOMY` selectors registry (orchestrator-owned).
 */

import type { AutonomyLever, AutonomyTier } from './types/autonomy.types';
import type { AutonomyGateId } from './types/gates.types';

export const AUTONOMY_TEST_IDS = {
  page: 'autonomy-page',
  lever: (lever: AutonomyLever | string) => `autonomy-lever-${lever}`,
  tierOption: (lever: AutonomyLever | string, tier: AutonomyTier | string) =>
    `autonomy-tier-option-${lever}-${tier}`,
  leverLockReason: (lever: AutonomyLever | string) => `autonomy-lever-lock-reason-${lever}`,
  killSwitch: 'autonomy-kill-switch',
  killSwitchConfirm: 'autonomy-kill-switch-confirm',
  killSwitchDialog: 'autonomy-kill-switch-dialog',
  haltedBanner: 'autonomy-halted-banner',
  rollbackLink: (id: string) => `autonomy-rollback-link-${id}`,
  checklistItem: (id: string) => `autonomy-checklist-item-${id}`,
  gate: (gateId: AutonomyGateId | string) => `autonomy-gate-${gateId}`,
  gateEvidence: (gateId: AutonomyGateId | string) => `autonomy-gate-evidence-${gateId}`,
  gatesRetry: 'autonomy-gates-retry',
  refusalPanel: 'autonomy-refusal-panel',
  refusalAttempt: (index: number) => `autonomy-refusal-attempt-${index}`,
  g9Chip: 'autonomy-g9-chip',
  liftChip: 'autonomy-lift-chip',
  actionError: 'autonomy-action-error',
  retry: 'autonomy-retry',
  /**
   * AC-EH3 read-error banner ("Autonomy settings could not be read …").
   * Own id: the edge-states mock annotated this element with the actionError
   * id, but the banner can coexist with a real save-action error alert —
   * one id per element keeps selectors unique.
   */
  readErrorBanner: 'autonomy-read-error-banner',
} as const;
